import React, { useEffect, useMemo, useRef, useState } from "react";

import { Card, CardTitle, Dot, Icon } from "./ui";
import MiniMap from "./MiniMap";
import useLiveWatch from "../../../hooks/useLiveWatch";

/*
  「안전 위반 알림」 — 개편 대시보드의 가운데 카드.

  이 화면의 성격이 «관제»가 아니라 «조치 관리»라서, 가운데에는 아무 영상이나 틀어두는 게
  아니라 지금 사람이 처리해야 할 한 건을 놓는다. 미확인 위반을 최신순으로 한 건씩 넘긴다.

  영상:
    · 그 카메라가 지금 송출 중이면 기본이 «실시간». 관리자가 알고 싶은 건
      "지금도 안 쓰고 있나?"지, 몇 분 전 사진이 아니다.
    · 꺼져 있으면 자동으로 «위반 순간» 캡처로 떨어진다. 버튼으로 둘을 오간다.
    · 실시간을 트는 동안 watch start/ping 을 보낸다(서버 시청자 집계).
*/

function fmtClock(iso) {
  const s = String(iso || "");
  const m = s.match(/(\d{2}):(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}:${m[3]}` : "-";
}

function minutesSince(iso) {
  const t = Date.parse(String(iso || "").replace(" ", "T"));
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 60000));
}

function fmtElapsed(min) {
  if (min == null) return "-";
  if (min < 60) return `${min}분`;
  const h = Math.floor(min / 60);
  return `${h}시간 ${min % 60}분`;
}

/*
  자주 쓰는 조치 내용. 매번 손으로 타자 치게 하면 결국 아무도 안 적거나
  «확인»만 반복해 적어 이력이 쓸모없어진다. 한 번 눌러 고르고 필요하면 고친다.
*/
const QUICK_NOTES = ["현장 통화 조치", "착용 확인함", "작업 중지 지시", "재발 경고"];

function Row({ k, children }) {
  return (
    <div className="flex items-center text-[13px]">
      <span className="text-slate-500 dark:text-slate-400">{k}</span>
      <span className="ml-auto font-semibold text-slate-900 dark:text-white">{children}</span>
    </div>
  );
}

export default function ViolationFocus({
  violations = [], // 미확인(open) 위반 — 최신순
  allToday = [], // 오늘 전체 위반(누적 횟수 계산용)
  cameras = [],
  statusMap = {},
  loading = false,
  index = 0, // 대기열에서 고른 번호(바깥이 쥐고 있다)
  onIndexChange,
  onChanged, // 확인/오탐 처리 후 목록 다시 읽기
  onNavigate,
  picked = null, // 아래 줄에서 고른 카메라 — 있으면 그 카메라를 튼다
  autoPicked = false, // 사용자가 고른 게 아니라 «위반이 없어 자동으로» 띄운 경우
  onClearPick,
  shotRequest = 0, // 대기열에서 고르면 올라간다 → 캡처로 전환
}) {
  const idx = index;
  const setIdx = (next) =>
    onIndexChange?.(typeof next === "function" ? next(index) : next);
  const [mode, setMode] = useState("auto"); // auto | live | shot
  const [busy, setBusy] = useState("");
  const [ackOpen, setAckOpen] = useState(false); // 조치 내용 입력 중
  const [note, setNote] = useState("");
  const [clip, setClip] = useState(null); // {videoUrl, seekOffsetSec} | {error}
  const [tick, setTick] = useState(0);
  const lastIdRef = useRef("");

  /*
    새 위반이 «들어오는 순간»을 눈에 띄게 한다.
    예전 «위험 감지» 카드처럼 숫자가 올라가면서 빨갛게 깜빡여야 한다 —
    가만히 숫자만 바뀌면 관리자가 못 보고 지나친다.
    5초간 세게 깜빡이고, 그 뒤에는 미확인이 남아 있는 동안 은은하게 계속 깜빡인다.
  */
  const [flashing, setFlashing] = useState(false);
  const prevCountRef = useRef(null);

  // 경과 시간이 멈춰 있으면 "오래된 화면"처럼 보인다. 1분마다 다시 그린다.
  useEffect(() => {
    const t = setInterval(() => setTick((v) => v + 1), 60000);
    return () => clearInterval(t);
  }, []);

  const list = Array.isArray(violations) ? violations : [];
  const safeIdx = list.length ? Math.min(idx, list.length - 1) : 0;
  const current = list[safeIdx] || null;

  useEffect(() => {
    const n = list.length;
    const prev = prevCountRef.current;
    prevCountRef.current = n;

    // 첫 로딩(null)에는 울리지 않는다. 줄어들 때(확인 처리)도 울리지 않는다.
    if (prev == null || n <= prev) return undefined;

    setFlashing(true);
    const t = setTimeout(() => setFlashing(false), 5000);
    return () => clearTimeout(t);
  }, [list.length]);

  // 목록이 바뀌어 보고 있던 건이 사라지면 맨 앞으로.
  useEffect(() => {
    const id = current?.id || "";
    if (id !== lastIdRef.current) {
      lastIdRef.current = id;
      setMode("auto");
      setClip(null);
      setAckOpen(false);
      setNote("");
    }
  }, [current?.id]);

  /*
    대기열에서 한 건을 고르면 «그 순간»을 보려는 것이다 — 실시간이 아니라 캡처를 띄운다.
    고른 뒤에 다시 실시간을 보고 싶으면 오른쪽 위 «실시간» 버튼을 누르면 된다.
  */
  useEffect(() => {
    if (shotRequest > 0) setMode("shot");
  }, [shotRequest]);

  useEffect(() => {
    // 목록이 줄어 범위를 벗어나면 맨 앞으로 되돌린다.
    if (idx > 0 && idx > list.length - 1) onIndexChange?.(0);
  }, [list.length, idx, onIndexChange]);

  const deviceId = String(current?.siteId || "").trim();

  const camera = useMemo(() => {
    if (!deviceId) return null;
    const arr = Array.isArray(cameras) ? cameras : [];
    return (
      arr.find((c) => String(c?.streamKey || "").trim() === deviceId) || null
    );
  }, [cameras, deviceId]);

  const meta = useMemo(() => {
    if (!camera) return {};
    const keys = [camera.camIdx, camera.id, camera.camNo, camera.cameraId, camera.streamKey]
      .map((v) => String(v ?? "").trim())
      .filter(Boolean);
    for (const k of keys) if (statusMap[k]) return statusMap[k];
    return {};
  }, [camera, statusMap]);

  const isOnline = String(meta?.liveStatus || "").toUpperCase() === "ONLINE";
  const webRtcUrl = String(camera?.webRtcUrl || "").trim();
  const canLive = isOnline && !!webRtcUrl;

  const showLive = mode === "live" || (mode === "auto" && canLive);

  // 아래 줄에서 카메라를 고르면 그 카메라를 튼다. 아니면 위반 카메라.
  const watchKey = picked ? picked.streamKey : deviceId;
  const watchOn = picked ? Boolean(picked.isOnline && picked.webRtcUrl) : showLive && canLive;
  useLiveWatch(watchKey, watchOn, "dashboard-focus");

  // tick 이 바뀔 때마다 다시 계산되도록 일부러 메모하지 않는다.
  const elapsed = minutesSince(current?.timestamp);
  void tick;

  // 같은 카메라가 오늘 몇 번째인지 — "또 저 사람이네"가 바로 보여야 한다.
  const todayCount = useMemo(() => {
    if (!deviceId) return 0;
    return (Array.isArray(allToday) ? allToday : []).filter(
      (v) => String(v?.siteId || "").trim() === deviceId
    ).length;
  }, [allToday, deviceId]);

  // 꺼진 카메라는 배터리를 보고하지 않고 0 으로 들어온다 — «0%»로 띄우면 오해를 부른다.
  const battery = (() => {
    const n = Number(meta?.battery);
    return isOnline && Number.isFinite(n) && n > 0 && n <= 100 ? n : null;
  })();

  const workerName =
    String(meta?.workerName || "").trim() ||
    String(current?.siteLabel || "").trim() ||
    "작업자 미지정";

  const subLine = [meta?.branchName, meta?.teamName, meta?.vehicleNo]
    .map((v) => String(v || "").trim())
    .filter(Boolean)
    .join(" · ") || String(current?.siteLabel || deviceId || "");

  const rawIdx = Number(String(current?.id || "").replace(/^evt_/, "")) || 0;

  /*
    확인 처리에는 «조치 내용»이 반드시 들어가야 한다.
    한때 여기서 "대시보드에서 확인"이라는 문구를 자동으로 넣고 바로 확인시켰는데,
    그러면 기록은 남아도 «무엇을 했는지»가 전부 같은 문장이라 이력이 쓸모없어진다.
    (누가·언제·무엇을 조치했는지가 삼우 납품 시 감사 대상이다)
  */
  const act = async (kind, text) => {
    if (!rawIdx || busy) return;

    if (kind === "ack") {
      const trimmed = String(text || "").trim();
      if (!trimmed) return; // 사유 없이는 확인되지 않는다
    }

    setBusy(kind);
    try {
      const res =
        kind === "ack"
          ? await fetch("/api/ppe-ack", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({ id: rawIdx, note: String(text || "").trim() }),
            })
          : await fetch("/api/event/false-positive", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({ id: rawIdx, type: "PPE_VIOLATION", value: true }),
            });

      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) {
        window.alert(data?.message || "처리에 실패했습니다.");
        return;
      }

      setAckOpen(false);
      setNote("");
      setIdx(0);
      onChanged?.();
    } catch (err) {
      console.error("위반 처리 실패", err);
      window.alert("처리 중 오류가 발생했습니다.");
    } finally {
      setBusy("");
    }
  };

  const openClip = async () => {
    if (!rawIdx || busy) return;
    setBusy("clip");
    try {
      const res = await fetch(`/api/event/${rawIdx}/clip`, {
        credentials: "include",
        cache: "no-store",
      });
      const j = await res.json();
      setClip(
        j?.hasClip && j?.videoUrl
          ? { videoUrl: j.videoUrl, seek: Number(j.seekOffsetSec || 0) }
          : { error: j?.message || "해당 구간 녹화를 찾지 못했습니다." }
      );
    } catch {
      setClip({ error: "영상 조회에 실패했습니다." });
    } finally {
      setBusy("");
    }
  };

  // 고른 카메라가 오늘 걸린 내역(조치 완료분 포함).
  const pickedToday = picked
    ? (Array.isArray(allToday) ? allToday : []).filter(
        (v) => String(v?.siteId || "").trim() === picked.streamKey
      )
    : [];

  // ── 아래 줄에서 고른 카메라 보기 ─────────────────────────────────────
  //   위반 화면을 잠시 비켜두고 그 카메라만 본다. 새 위반이 들어오면
  //   바깥(DashboardPage)이 선택을 풀어 위반 화면으로 되돌린다.
  if (picked) {
    return (
      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <CardTitle
          title={picked.name}
          right={
            <>
              {autoPicked && (
                <span className="flex items-center gap-1.5 text-[12px] font-semibold text-emerald-600 dark:text-emerald-400">
                  <Dot tone="ok" />
                  미조치 위반 없음
                </span>
              )}
              <span className="flex items-center gap-1.5 text-[12px] font-medium text-slate-500 dark:text-slate-400">
                <Dot tone={picked.isOnline ? "ok" : "off"} />
                {picked.isOnline ? "송출 중" : "오프라인"}
              </span>
              {!autoPicked && (
                <button
                  type="button"
                  onClick={onClearPick}
                  className="rounded-md border border-slate-300 px-2.5 py-1 text-[12px] font-medium text-slate-600 transition hover:bg-slate-50 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/5"
                >
                  위반 화면으로
                </button>
              )}
            </>
          }
        />

        <div className="flex min-h-0 flex-1">
          <div className="relative min-w-0 flex-1 bg-slate-900">
            {picked.isOnline && picked.webRtcUrl ? (
              <iframe
                key={`picked-${picked.streamKey}`}
                title={`picked-live-${picked.streamKey}`}
                src={picked.webRtcUrl}
                className="h-full w-full"
                style={{ border: 0, background: "black" }}
                allow="autoplay; fullscreen; encrypted-media"
              />
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-2 text-white/45">
                <Icon.Camera size={28} />
                <span className="text-[12.5px]">송출 중이 아닙니다</span>
              </div>
            )}

            <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent px-3.5 pb-2.5 pt-10">
              <div className="text-[12px] font-medium text-white/95">
                {picked.isOnline ? "실시간" : "오프라인"} · {picked.sub}
              </div>
            </div>
          </div>

          <div className="flex w-[296px] shrink-0 flex-col border-l border-slate-200 p-[18px] dark:border-white/10">
            <div className="truncate text-[17px] font-bold tracking-[-0.3px] text-slate-900 dark:text-white">
              {picked.name}
            </div>
            <div className="mt-1 truncate text-[12.5px] text-slate-500 dark:text-slate-400">
              {picked.sub}
            </div>

            <div className="mt-4 flex flex-col gap-[11px]">
              <Row k="상태">{picked.isOnline ? "송출 중" : "오프라인"}</Row>
              <Row k="배터리">
                {picked.battery == null ? (
                  <span className="text-slate-400">—</span>
                ) : (
                  <span className="flex items-center gap-[7px]">
                    <span className="inline-block h-[7px] w-[30px] overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
                      <i
                        className={`block h-full rounded-full ${
                          picked.battery <= 20
                            ? "bg-rose-500"
                            : picked.battery <= 40
                            ? "bg-amber-500"
                            : "bg-emerald-500"
                        }`}
                        style={{ width: `${picked.battery}%` }}
                      />
                    </span>
                    {picked.battery}%
                  </span>
                )}
              </Row>
              <Row k="오늘 위반">
                <span className={pickedToday.length > 0 ? "text-amber-600 dark:text-amber-300" : ""}>
                  {pickedToday.length}회
                </span>
              </Row>
            </div>

            {/*
              카메라만 고른 화면은 숫자 세 줄뿐이라 아래가 휑했다.
              그 카메라가 «오늘 무엇을 걸렸는지»를 바로 보여준다 — 누르면 그 건으로 넘어간다.
            */}
            {pickedToday.length > 0 && (
              <div className="mt-4 shrink-0">
                <div className="mb-1.5 text-[11.5px] font-medium text-slate-500 dark:text-slate-400">
                  오늘 위반 내역
                </div>
                <div className="max-h-[132px] overflow-y-auto rounded-lg border border-slate-200 dark:border-white/10">
                  {pickedToday.slice(0, 20).map((v) => {
                    const openIdx = list.findIndex((o) => o.id === v.id);
                    const done = v.status !== "open";
                    return (
                      <button
                        key={v.id}
                        type="button"
                        disabled={openIdx < 0}
                        onClick={() => {
                          onClearPick?.();
                          onIndexChange?.(openIdx);
                        }}
                        className="flex w-full items-center gap-2 border-b border-slate-100 px-2.5 py-[7px] text-left transition last:border-0 enabled:hover:bg-slate-50 disabled:cursor-default dark:border-white/5 dark:enabled:hover:bg-white/5"
                      >
                        <span
                          className={`h-[5px] w-[5px] shrink-0 rounded-full ${
                            done ? "bg-slate-300 dark:bg-slate-600" : "bg-rose-500"
                          }`}
                        />
                        <span className="truncate text-[12px] font-medium text-slate-700 dark:text-slate-200">
                          {v.itemLabel}
                        </span>
                        <span className="ml-auto shrink-0 text-[11px] tabular-nums text-slate-400">
                          {fmtClock(v.timestamp).slice(0, 5)}
                        </span>
                        <span
                          className={`shrink-0 text-[10.5px] font-semibold ${
                            done ? "text-slate-400" : "text-rose-500"
                          }`}
                        >
                          {done ? "조치" : "미조치"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <MiniMap streamKey={picked.streamKey} onOpenMap={() => onNavigate?.("map")} />

            <div className="mt-auto pt-4">
              <button
                type="button"
                onClick={() => onNavigate?.("live")}
                className="flex h-10 w-full items-center justify-center gap-[7px] rounded-lg border border-slate-300 text-[13px] font-medium text-slate-700 transition hover:bg-slate-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/5"
              >
                실시간 관제에서 크게 보기
                <Icon.Right size={14} />
              </button>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // ── 빈 상태 ─────────────────────────────────────────────────────────
  if (!loading && !current) {
    return (
      <Card className="flex min-h-0 flex-1 flex-col">
        <CardTitle
          title="안전 위반 알림"
          right={
            <span className="text-[12.5px] font-medium text-slate-400">0건</span>
          }
        />
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-1.5 p-6 text-center">
          <div className="text-[15px] font-semibold text-slate-700 dark:text-slate-200">
            미확인 위반이 없습니다
          </div>
          <div className="text-[12.5px] text-slate-400">
            새 위반이 감지되면 이 자리에 올라옵니다.
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      className={`relative flex min-h-0 flex-1 flex-col overflow-hidden ${
        list.length > 0
          ? "border-rose-300 shadow-[0_0_0_3px_rgba(248,113,113,0.15)] dark:border-rose-500/50"
          : ""
      }`}
    >
      {/*
        미확인 위반이 남아 있는 동안 테두리가 깜빡인다.
        예전 «위험 감지» 카드에 있던 경보 효과 — 개편하면서 빠뜨렸다.
        가만히 있으면 관리자가 화면을 안 보고 지나친다.
      */}
      {/*
        미확인 위반이 남아 있는 «내내» 테두리가 굵게 깜빡인다.
        새 건이 들어온 직후 5초만 더 빠르게 뛴다(터진 순간을 놓치지 않게).
        영상 위를 빨갛게 덮지 않는다 — 정작 봐야 할 화면을 가린다.
      */}
      {list.length > 0 && (
        <span
          className={`pointer-events-none absolute inset-0 z-10 rounded-xl border-[3px] border-rose-500 ${
            flashing
              ? "animate-[pulse_0.6s_ease-in-out_infinite]"
              : "animate-[pulse_1.6s_ease-in-out_infinite]"
          }`}
        />
      )}

      <CardTitle
        title="안전 위반 알림"
        right={
          <>
            <span className="relative inline-flex items-center">
              {flashing && (
                <span className="absolute inset-0 -m-1 animate-ping rounded-full bg-rose-400/40" />
              )}
              <span
                className={`relative rounded-full px-2 py-[3px] text-[12.5px] font-bold tabular-nums transition ${
                  flashing
                    ? "bg-rose-600 text-white"
                    : "text-rose-600 dark:text-rose-400"
                }`}
              >
                미확인 {list.length}건
              </span>
            </span>
            <button
              type="button"
              onClick={() => onNavigate?.("events")}
              className="flex items-center gap-1 text-[12.5px] font-medium text-slate-500 transition hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
            >
              전체 이벤트
              <Icon.Right size={13} />
            </button>
          </>
        }
      />

      <div className="flex min-h-0 flex-1">
        {/* 영상 / 캡처 */}
        <div className="relative min-w-0 flex-1 bg-slate-900">
          {loading ? (
            <div className="flex h-full items-center justify-center text-[13px] text-white/60">
              위반 내역 불러오는 중…
            </div>
          ) : showLive && canLive ? (
            <iframe
              key={`focus-${deviceId}`}
              title={`focus-live-${deviceId}`}
              src={webRtcUrl}
              className="h-full w-full"
              style={{ border: 0, background: "black" }}
              allow="autoplay; fullscreen; encrypted-media"
            />
          ) : current?.photoUrl ? (
            <img
              src={current.photoUrl}
              alt="위반 캡처"
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-white/50">
              <Icon.Image size={28} />
              <span className="text-[12.5px]">캡처 이미지가 없습니다</span>
            </div>
          )}

          {/* 좌상단 — 위반 항목 */}
          <div
            className={`pointer-events-none absolute left-3.5 top-3.5 z-[6] flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-[12px] font-semibold text-white shadow-md ${
              flashing
                ? "bg-rose-600 animate-[pulse_0.6s_ease-in-out_infinite]"
                : "bg-rose-600"
            }`}
          >
            <Icon.Alert size={14} />
            {current?.itemLabel || "안전장구 미착용"}
          </div>

          {/* 우상단 — 넘기기만. 실시간/캡처 전환은 오른쪽 패널로 옮겼다(너무 작아 안 보였다). */}
          <div className="absolute right-3.5 top-3.5 flex items-center gap-1.5">
            {list.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => setIdx((v) => (v - 1 + list.length) % list.length)}
                  className="flex h-[30px] w-[30px] items-center justify-center rounded-lg bg-white/90 text-slate-700 hover:bg-white"
                  aria-label="이전 위반"
                >
                  <Icon.Left size={14} />
                </button>
                <span className="flex h-[30px] items-center rounded-lg bg-black/70 px-2.5 text-[12px] font-semibold text-white">
                  {safeIdx + 1} / {list.length}
                </span>
                <button
                  type="button"
                  onClick={() => setIdx((v) => (v + 1) % list.length)}
                  className="flex h-[30px] w-[30px] items-center justify-center rounded-lg bg-white/90 text-slate-700 hover:bg-white"
                  aria-label="다음 위반"
                >
                  <Icon.Right size={14} />
                </button>
              </>
            )}
          </div>

          {/* 하단 자막 */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent px-3.5 pb-2.5 pt-10">
            <div className="text-[12px] font-medium text-white/95">
              {showLive && canLive
                ? `실시간 · ${subLine}`
                : `${String(current?.timestamp || "").replace("T", " ").slice(0, 19)} · ${subLine}`}
            </div>
          </div>
        </div>

        {/* 상세 + 조치 */}
        <div className="flex w-[320px] shrink-0 flex-col border-l border-slate-200 p-[18px] dark:border-white/10">
          <div className="flex items-center gap-2">
            <div className="truncate text-[17px] font-bold tracking-[-0.3px] text-slate-900 dark:text-white">
              {workerName}
            </div>
            <span className="flex shrink-0 items-center gap-1.5 text-[12px] font-medium text-slate-500 dark:text-slate-400">
              <Dot tone={isOnline ? "ok" : "off"} />
              {isOnline ? "송출 중" : "오프라인"}
            </span>
          </div>
          <div className="mt-1 truncate text-[12.5px] text-slate-500 dark:text-slate-400">
            {subLine}
          </div>

          {/*
            «실시간 / 위반 순간» 전환. 영상 위 작은 알약으로 뒀더니 안 보였고,
            캡처를 보다가 실시간으로 어떻게 돌아가는지 헷갈린다는 지적을 받았다.
            이름 바로 아래 큼직한 2칸 스위치로 옮긴다.
          */}
          {canLive && (
            <div className="mt-3 flex rounded-lg border border-slate-200 bg-slate-100 p-[3px] dark:border-white/10 dark:bg-white/5">
              <button
                type="button"
                onClick={() => setMode("live")}
                className={`flex h-[34px] flex-1 items-center justify-center gap-1.5 rounded-md text-[13px] font-semibold transition ${
                  showLive
                    ? "bg-white text-slate-900 shadow-sm dark:bg-[#1d2939] dark:text-white"
                    : "text-slate-500 hover:text-slate-700 dark:text-slate-400"
                }`}
              >
                <span className="relative flex h-2 w-2">
                  {showLive && (
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-500 opacity-75" />
                  )}
                  <span
                    className={`relative inline-flex h-2 w-2 rounded-full ${
                      showLive ? "bg-rose-500" : "bg-slate-300 dark:bg-slate-600"
                    }`}
                  />
                </span>
                실시간
              </button>
              <button
                type="button"
                onClick={() => setMode("shot")}
                className={`flex h-[34px] flex-1 items-center justify-center gap-1.5 rounded-md text-[13px] font-semibold transition ${
                  !showLive
                    ? "bg-white text-slate-900 shadow-sm dark:bg-[#1d2939] dark:text-white"
                    : "text-slate-500 hover:text-slate-700 dark:text-slate-400"
                }`}
              >
                <Icon.Image size={14} />
                위반 순간
              </button>
            </div>
          )}

          <div className="mt-3.5 flex flex-col gap-[11px]">
            <Row k="발생 시각">{fmtClock(current?.timestamp)}</Row>
            <Row k="경과">
              <span className={elapsed != null && elapsed >= 10 ? "text-rose-600 dark:text-rose-300" : ""}>
                {fmtElapsed(elapsed)}
              </span>
            </Row>
            <Row k="위반 항목">{current?.itemLabel || "-"}</Row>
            <Row k="오늘 누적">
              <span className={todayCount >= 3 ? "text-amber-600 dark:text-amber-300" : ""}>
                {todayCount}회
              </span>
            </Row>
            <Row k="배터리">
              {battery == null ? (
                <span className="text-slate-400">-</span>
              ) : (
                <span className="flex items-center gap-[7px]">
                  <span className="inline-block h-[7px] w-[30px] overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
                    <i
                      className={`block h-full rounded-full ${
                        battery <= 20 ? "bg-rose-500" : battery <= 40 ? "bg-amber-500" : "bg-emerald-500"
                      }`}
                      style={{ width: `${battery}%` }}
                    />
                  </span>
                  {battery}%
                </span>
              )}
            </Row>
          </div>

          <MiniMap streamKey={deviceId} onOpenMap={() => onNavigate?.("map")} />

          <div className="mt-auto pt-4">
            {ackOpen ? (
              <div>
                <div className="mb-2 text-[11.5px] font-medium text-slate-500 dark:text-slate-400">
                  조치 내용 (이력에 남습니다)
                </div>

                <div className="mb-2 flex flex-wrap gap-1.5">
                  {QUICK_NOTES.map((q) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => setNote(q)}
                      className={`rounded-md border px-2 py-[3px] text-[11.5px] transition ${
                        note === q
                          ? "border-blue-500 bg-blue-50 font-medium text-blue-700 dark:bg-blue-500/10 dark:text-blue-300"
                          : "border-slate-200 text-slate-600 hover:border-slate-300 dark:border-white/10 dark:text-slate-300"
                      }`}
                    >
                      {q}
                    </button>
                  ))}
                </div>

                <input
                  type="text"
                  value={note}
                  autoFocus
                  maxLength={200}
                  onChange={(e) => setNote(e.target.value)}
                  onKeyDown={(e) => {
                    // 한글 조합 중 엔터는 무시(조합 확정용) — 중복 제출 방지.
                    if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                      e.preventDefault();
                      act("ack", note);
                    }
                    if (e.key === "Escape") setAckOpen(false);
                  }}
                  placeholder="예) 현장 통화, 착용 확인함"
                  className="h-10 w-full rounded-lg border border-slate-300 px-3 text-[13px] outline-none transition focus:border-blue-500 dark:border-white/15 dark:bg-[#0B1220] dark:text-white"
                />

                <div className="mt-2 grid grid-cols-[1fr_auto] gap-2">
                  <button
                    type="button"
                    disabled={!!busy || !note.trim()}
                    onClick={() => act("ack", note)}
                    className="flex h-10 items-center justify-center gap-[7px] rounded-lg bg-blue-600 text-[13.5px] font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Icon.Check size={16} />
                    {busy === "ack" ? "처리 중…" : "확인 처리"}
                  </button>
                  <button
                    type="button"
                    disabled={!!busy}
                    onClick={() => {
                      setAckOpen(false);
                      setNote("");
                    }}
                    className="h-10 rounded-lg border border-slate-300 px-4 text-[13px] font-medium text-slate-600 transition hover:bg-slate-50 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/5"
                  >
                    취소
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  disabled={!!busy}
                  onClick={() => setAckOpen(true)}
                  className="flex h-10 items-center justify-center gap-[7px] rounded-lg bg-blue-600 text-[13.5px] font-semibold text-white transition hover:bg-blue-700 disabled:opacity-60"
                >
                  <Icon.Check size={16} />
                  확인 처리
                </button>

                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    disabled={!!busy}
                    onClick={openClip}
                    className="flex h-10 items-center justify-center gap-[7px] rounded-lg border border-slate-300 text-[13px] font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/5"
                  >
                    <Icon.Film size={15} />
                    30초 영상
                  </button>
                  <button
                    type="button"
                    disabled={!!busy}
                    onClick={() => act("fp")}
                    className="flex h-10 items-center justify-center gap-[7px] rounded-lg border border-slate-300 text-[13px] font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/5"
                  >
                    <Icon.Flag size={15} />
                    오탐 신고
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 30초 영상 모달 */}
      {clip && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-6"
          onClick={() => setClip(null)}
          role="presentation"
        >
          <div
            className="w-full max-w-[880px] overflow-hidden rounded-2xl bg-[#0B1220]"
            onClick={(e) => e.stopPropagation()}
            role="presentation"
          >
            <div className="flex items-center gap-3 px-5 py-3.5">
              <div className="text-[14px] font-semibold text-white">
                위반 앞뒤 30초 · {workerName}
              </div>
              <button
                type="button"
                onClick={() => setClip(null)}
                className="ml-auto rounded-lg bg-white/10 px-3 py-1.5 text-[12.5px] font-semibold text-white hover:bg-white/15"
              >
                닫기
              </button>
            </div>
            {clip.error ? (
              <div className="px-5 pb-6 text-[13px] text-white/70">{clip.error}</div>
            ) : (
              <video
                src={clip.videoUrl}
                className="max-h-[70vh] w-full bg-black"
                controls
                autoPlay
                onLoadedMetadata={(e) => {
                  const s = Number(clip.seek || 0);
                  if (s > 0) e.currentTarget.currentTime = s;
                }}
              >
                <track kind="captions" />
              </video>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
